import { useId, useState, type KeyboardEvent } from 'react';

type UseComboboxOptions<T> = {
  items: T[];
  term: string;
  minTermLength?: number;
  onSelect: (item: T) => void;
  isOpen?: boolean;
  onRequestClose?: () => void;
  keepsOpenAfterSelecting?: (item: T) => boolean;
};

function comboboxInputProps(
  isOpen: boolean,
  listboxId: string,
  safeActiveIndex: number
) {
  return {
    role: 'combobox',
    'aria-expanded': isOpen,
    'aria-controls': listboxId,
    'aria-autocomplete': 'list',
    'aria-activedescendant':
      isOpen && safeActiveIndex >= 0
        ? `${listboxId}-option-${safeActiveIndex}`
        : undefined,
  } as const;
}

export type ComboboxInputProps = ReturnType<typeof comboboxInputProps>;

// Headless state and ARIA wiring for a text input with a suggestions listbox.
// The active index is clamped at render so it never references a removed item.
// Passing `isOpen` hands the panel to the caller: the derived form cannot stay
// open on zero items, which a panel with its own empty state has to do.
export function useCombobox<T>({
  items,
  term,
  minTermLength = 1,
  onSelect,
  isOpen: isOpenFromCaller,
  onRequestClose,
  keepsOpenAfterSelecting,
}: UseComboboxOptions<T>) {
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();

  const isOpen =
    isOpenFromCaller ??
    (isFocused && term.trim().length >= minTermLength && items.length > 0);
  const safeActiveIndex =
    isOpen && activeIndex < items.length ? activeIndex : -1;

  function select(item: T) {
    onSelect(item);
    if (keepsOpenAfterSelecting?.(item)) return;
    setIsFocused(false);
    setActiveIndex(-1);
    onRequestClose?.();
  }

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlur() {
    setIsFocused(false);
    setActiveIndex(-1);
  }

  // Call from the input's onChange: typing resets the highlight and re-opens
  // the panel even right after a select.
  function handleInputChange() {
    setActiveIndex(-1);
    setIsFocused(true);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (!isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(
        safeActiveIndex < items.length - 1 ? safeActiveIndex + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(
        safeActiveIndex > 0 ? safeActiveIndex - 1 : items.length - 1
      );
    } else if (event.key === 'Enter' && safeActiveIndex >= 0) {
      event.preventDefault();
      const active = items[safeActiveIndex];
      if (active !== undefined) select(active);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsFocused(false);
      setActiveIndex(-1);
      onRequestClose?.();
    }
  }

  const inputProps = comboboxInputProps(isOpen, listboxId, safeActiveIndex);

  function optionProps(index: number) {
    return {
      id: `${listboxId}-option-${index}`,
      role: 'option',
      'aria-selected': index === safeActiveIndex,
    } as const;
  }

  return {
    isOpen,
    listboxId,
    safeActiveIndex,
    select,
    handleFocus,
    handleBlur,
    handleInputChange,
    handleKeyDown,
    inputProps,
    optionProps,
  };
}
